const REQUIRED_FIELDS = {
  customerSubdomain: 'string',
  userId: 'number',
}

const OPTIONAL_FIELDS = {
  grade: 'string',
  unit: 'string',
  section: 'string',
  lesson: 'string',
  standards: 'array',
  currentCurricula: 'array',
  availableCurricula: 'array',
  curriculumCustomerId: 'number',
}

const DEFAULTS = {
  grade: null,
  unit: null,
  section: null,
  lesson: null,
  standards: [],
  currentCurricula: [],
  availableCurricula: [],
  curriculumCustomerId: null,
}

function validateContext(context) {
  if (!context || typeof context !== 'object') {
    throw new Error('Context must be a non-null object')
  }

  for (const [field, expectedType] of Object.entries(REQUIRED_FIELDS)) {
    if (context[field] === undefined || context[field] === null) {
      throw new Error(`Missing required field: ${field}`)
    }

    if (typeof context[field] !== expectedType) {
      throw new Error(`${field} must be a ${expectedType}`)
    }
  }

  for (const [field, expectedType] of Object.entries(OPTIONAL_FIELDS)) {
    if (context[field] !== undefined && context[field] !== null) {
      if (expectedType === 'array') {
        if (!Array.isArray(context[field])) {
          throw new Error(`${field} must be an ${expectedType}`)
        }
      } else if (typeof context[field] !== expectedType) {
        throw new Error(`${field} must be a ${expectedType}`)
      }
    }
  }
}

function normalizeContext(context) {
  return {
    customerSubdomain: context.customerSubdomain,
    userId: context.userId,
    grade: context.grade ?? DEFAULTS.grade,
    unit: context.unit ?? DEFAULTS.unit,
    section: context.section ?? DEFAULTS.section,
    lesson: context.lesson ?? DEFAULTS.lesson,
    standards: context.standards ?? DEFAULTS.standards,
    currentCurricula: context.currentCurricula ?? DEFAULTS.currentCurricula,
    availableCurricula: context.availableCurricula ?? DEFAULTS.availableCurricula,
    curriculumCustomerId: context.curriculumCustomerId ?? DEFAULTS.curriculumCustomerId,
  }
}

function getAccessToken() {
  const match = document.cookie.match(/(^|;\s*)access_token=([^;]+)/)
  return match ? match[2] : null
}

function loadScript(url) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    script.setAttribute('data-curriculum-widget', 'true')
    script.onload = resolve
    script.onerror = () => reject(new Error(`Failed to load widget script: ${url}`))
    document.head.appendChild(script)
  })
}

function createWidgetClient({ scriptUrl } = {}) {
  let initialized = false
  let context = null
  const listeners = {}

  return {
    init(ctx) {
      if (initialized) {
        return Promise.resolve()
      }

      try {
        validateContext(ctx)
      } catch (error) {
        return Promise.reject(error)
      }

      context = normalizeContext(ctx)

      const token = getAccessToken()
      if (!token) {
        return Promise.reject(new Error('JWT authentication failed: access_token cookie not found'))
      }

      const resolvedUrl = buildScriptUrl(context, scriptUrl)
      if (!resolvedUrl) {
        return Promise.reject(new Error('Widget script URL is not configured'))
      }

      return loadScript(resolvedUrl).then(() => {
        initialized = true
      })
    },

    isInitialized() {
      return initialized
    },

    on(eventType, handler) {
      if (!listeners[eventType]) {
        listeners[eventType] = []
      }
      listeners[eventType].push(handler)
    },

    off(eventType, handler) {
      if (!listeners[eventType]) return
      if (handler) {
        listeners[eventType] = listeners[eventType].filter((h) => h !== handler)
      } else {
        delete listeners[eventType]
      }
    },

    destroy() {
      const loadedScript = document.querySelector('script[data-curriculum-widget]')
      if (loadedScript) {
        loadedScript.remove()
      }

      Object.keys(listeners).forEach((key) => delete listeners[key])
      context = null
      initialized = false
    },
  }
}

function buildScriptUrl(context, baseUrl) {
  if (!baseUrl) return null

  const uri = new URL(baseUrl)
  uri.searchParams.set('customer', context.customerSubdomain)
  if (!uri.searchParams.has('containerId')) {
    uri.searchParams.set('containerId', 'pango-root')
  }
  return uri.toString()
}

export { createWidgetClient, validateContext, normalizeContext, getAccessToken, buildScriptUrl }
export default createWidgetClient
